import styled from 'styled-components';
import { sdsColors, sdsGrayscale } from '../../theme/sds-tokens';
import { chartCardSurfaceDark, chartMutedTextColor } from './charts/chartColors';

export const HomeContainer = styled.div<{ $isDark: boolean }>`
  --chart-card-surface: ${({ $isDark }) => ($isDark ? chartCardSurfaceDark : 'transparent')};

  padding: 0px 10px 10px 10px;
  display: flex;
  flex-direction: column;
  height: 100%;
  align-items: center;

  @media (max-width: 600px) {
    padding: 0px 8px 8px 8px;
  }
`;

export const DashboardContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-top: 8px;
  width: 100%;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
`;

export const SkeletonContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  box-shadow: rgba(60, 64, 67, 0.08) 0px 1px 3px 0px;
  padding: 16px 20px;
  border-radius: 12px;
  min-height: 110px;

  @media (max-width: 600px) {
    padding: 10px 12px;
    min-height: 80px;
    border-radius: 8px;
  }
`;

export const DashboardCard = styled.div`
  display: flex;
  flex-direction: column;
  border: 1.5px solid #8d8e8f;
  box-shadow: rgba(60, 64, 67, 0.08) 0px 1px 3px 0px;
  padding: 16px 20px;
  border-radius: 12px;
  min-height: 110px;

  @media (max-width: 600px) {
    padding: 10px 12px;
    min-height: 80px;
    border-radius: 8px;
  }

  > div > span {
    color: #209088;
    font-size: 14px;

    @media (max-width: 600px) {
      font-size: 11px;
    }
  }

  h1 {
    color: #7f8790;
    margin: 8px 0 0;
    font-size: 34px;
    line-height: 1;

    @media (max-width: 600px) {
      font-size: 22px;
      margin: 4px 0 0;
    }
  }

  p {
    margin: 8px 0 0;
    font-size: 20px;
    color: #7f8790;

    @media (max-width: 600px) {
      font-size: 14px;
      margin: 4px 0 0;
    }
  }
`;

export const DashboardCardNR1 = styled.div<{ themeState: { isDark: boolean } }>`
  display: grid;
  grid-template-columns: minmax(64px, auto) minmax(0, 1fr);
  align-items: center;
  column-gap: 16px;
  flex: 1 1 0;
  box-sizing: border-box;
  padding: 14px 16px 14px 18px;
  background: ${props => (props.themeState.isDark ? chartCardSurfaceDark : sdsGrayscale[0])};
  border: 1px solid ${props => (props.themeState.isDark ? 'rgba(255, 255, 255, 0.08)' : sdsGrayscale[20])};
  border-left: 4px solid #209088;
  border-radius: 12px;

  h1 {
    margin: 0;
    font-size: 32px;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
    color: ${props => (props.themeState.isDark ? sdsGrayscale[0] : sdsColors.textPrimary)};
  }

  h3 {
    margin: 0 0 4px 0;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.3;
    color: #209088;
  }

  p {
    margin: 0;
    font-size: 12px;
    line-height: 1.45;
    color: ${props => chartMutedTextColor(props.themeState.isDark)};
  }

  @media (max-width: 600px) {
    padding: 12px 12px 12px 14px;

    h1 {
      font-size: 26px;
    }

    h3 {
      font-size: 13px;
    }

    p {
      font-size: 11px;
    }
  }
`;

export const Nr1Layout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  @media (max-width: 800px) {
    gap: 8px;
  }
`;

export const Nr1TopRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 3fr) minmax(0, 1fr);
  grid-template-areas: 'cards line gender';
  gap: 16px;

  & > br {
    display: none;
  }

  & > [data-area='cards'] {
    grid-area: cards;
  }

  & > [data-area='line'] {
    grid-area: line;
  }

  & > [data-area='gender'] {
    grid-area: gender;
  }

  @media (max-width: 1280px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-areas:
      'cards gender'
      'line line';
  }

  @media (max-width: 800px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      'cards'
      'line'
      'gender';
    gap: 8px;
  }
`;

export const Nr1CardsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;

  @media (max-width: 800px) {
    gap: 8px;
  }
`;

export const Nr1BottomRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  & > br {
    display: none;
  }

  & > * {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    margin-top: 0;
  }

  @media (max-width: 1280px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 800px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
  }
`;

export const Nr1Card = styled.section<{ $isDark: boolean }>`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  min-width: 0;
  padding: 16px;
  background: ${({ $isDark }) => ($isDark ? chartCardSurfaceDark : sdsGrayscale[0])};
  border: 1px solid ${({ $isDark }) => ($isDark ? 'rgba(255, 255, 255, 0.08)' : sdsGrayscale[20])};
  border-radius: 12px;
`;


export const Graficos = styled.div`
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 8px;
  width: 100%;

  & > br {
    display: none;
  }

  & > * {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    margin-top: 0;
    grid-column: span 4;
  }

  & > [data-span='double'] {
    grid-column: span 8;
  }

  & > [data-span='half'] {
    grid-column: span 6;
  }

  & .MuiSkeleton-circular {
    max-width: 100%;
    height: auto !important;
    aspect-ratio: 1;
  }

  @media (max-width: 1280px) {
    & > *,
    & > [data-span] {
      grid-column: span 6;
    }
  }

  @media (max-width: 800px) {
    gap: 8px;

    & > *,
    & > [data-span] {
      grid-column: 1 / -1;
    }
  }
`;

export const GraficosRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 0.8fr) minmax(0, 1.4fr);
  gap: 16px;

  && {
    grid-column: 1 / -1;
  }

  & > br {
    display: none;
  }

  && > * {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    margin-top: 0;
  }

  &&& > * {
    padding: 16px 16px;

    @media (max-width: 480px) {
      padding: 18px 16px;
    }
  }

  @media (max-width: 1280px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    & > :last-child {
      grid-column: 1 / -1;
    }
  }

  @media (max-width: 800px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
  }
`;

export const GraficosStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  & > br {
    display: none;
  }

  & > * {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    margin-top: 0;
  }

  & > :first-child {
    flex: 1 1 auto;
  }
`;

export const GraficosPair = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  & > br {
    display: none;
  }

  & > * {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    margin-top: 0;
  }

  @media (max-width: 1280px) and (min-width: 801px), (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const Grafico = styled.div`
  background: var(--chart-card-surface, transparent);
  flex-wrap: wrap;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  align-items: center;
  justify-content: center;
  margin-top: 0.25rem;
  box-shadow: rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px;
  padding: 1.25rem;

  ${Graficos} & {
    padding: 0.75rem;
  }

  @media (max-width: 600px) {
    padding: 0.75rem;
    margin-top: 0.5rem;
    width: 100%;
    max-width: 100%;
  }
  
  h2,
  h3 {
    color: #54586e;

    @media (max-width: 600px) {
      font-size: 14px;
    }
  }
`;

export const UserArea = styled.div`
  width: 100%;
  font-size: 1.6rem;
  padding: 40px;

  @media (max-width: 600px) {
    padding: 16px;
    font-size: 1.2rem;
  }
`;

export const LegendContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
  padding-right: 8px;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 600px) {
    max-height: 250px;
  }
`;

export const AbsenteeismCharts = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: stretch;
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const AbsenteeismChartsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;

  & > * {
    height: auto;
  }

  & > :first-child {
    flex: 1 1 auto;
  }
`;
